import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import Button from '../ui/Button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show prompt after 30 seconds or on next visit
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setInstalling(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('pwa-prompt-dismissed', '1');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-50 flex items-start gap-3 rounded-xl border border-secondary-200 bg-white p-4 shadow-2xl dark:border-secondary-700 dark:bg-secondary-900 md:bottom-6 md:left-auto md:right-6 md:w-80"
      dir="rtl"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-950 dark:text-primary-300">
        <Download className='h-6 w-6' aria-hidden='true' />
      </div>
      <div className="flex-1 min-w-0">
        <p className="mb-0.5 text-sm font-semibold text-secondary-900 dark:text-white">ثبّت التطبيق</p>
        <p className="mb-3 text-xs text-secondary-500 dark:text-secondary-400">
          ثبّت Class-Tech على جهازك للوصول السريع دون متصفح
        </p>
        <div className="flex gap-2">
          <Button
            onClick={handleInstall}
            loading={installing}
            size='sm'
            className="flex-1"
          >
            تثبيت
          </Button>
          <Button
            onClick={handleDismiss}
            variant='secondary'
            size='sm'
          >
            لاحقاً
          </Button>
        </div>
      </div>
      <button
        type='button'
        onClick={handleDismiss}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-secondary-800 dark:hover:text-secondary-200"
        aria-label='إغلاق رسالة تثبيت التطبيق'
      >
        <X className='h-4 w-4' />
      </button>
    </div>
  );
};

export default InstallPrompt;
